import { Fragment } from 'react';

// Use unified date utils
import { moment } from '../../../utils/dateUtils';

import {
  Box,
  Table,
  TablePagination,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Card,
  CardContent,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
  Button,
} from '@mui/material';

// Icons
import CancelIcon from '@mui/icons-material/Cancel';
import CircleIcon from '@mui/icons-material/Circle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

// Hooks
import usePagination from '../../../hooks/usePagination';

// Styles
import { table_container_base } from '../../../static/styles';

const statusConfig = {
  'Active': { icon: CircleIcon, color: '#4caf50', bg: '#e8f5e9' },
  'Pending': { icon: CircleIcon, color: '#ff9800', bg: '#fff3e0' },
  'Cold': { icon: CircleIcon, color: '#42a5f5', bg: '#e3f2fd' },
  'Completed': { icon: CheckCircleIcon, color: '#4caf50', bg: '#e8f5e9' },
  'Cancelled': { icon: CancelIcon, color: '#f44336', bg: '#ffebee' },
  'Lost': { icon: CircleIcon, color: '#e91e63', bg: '#fce4ec' },
};

const MobileProposalCard = ({ proposal }) => {
  const status = statusConfig[proposal.status] || statusConfig['Active'];
  const StatusIcon = status.icon;
  
  return (
    <Card variant="outlined" sx={{ mb: 2, borderLeft: `4px solid ${status.color}` }}>
      <Link href={`/proposals/${proposal.id}`} underline="none">
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Chip 
                icon={<StatusIcon sx={{ fontSize: 14 }} />}
                label={proposal.status} 
                size="small" 
                sx={{ bgcolor: status.bg, color: status.color, fontWeight: 600 }} 
              />
              {proposal.additionalNotes?.payment?.completed && (
                <Chip
                  icon={<AttachMoneyIcon sx={{ fontSize: 14, color: '#4caf50 !important' }} />}
                  label="Paid"
                  size="small"
                  sx={{ height: 20, backgroundColor: '#e8f5e9', color: '#4caf50', fontWeight: 600, fontSize: 10 }}
                />
              )}
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1976d2' }}>
              {proposal.publicId}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <LocationOnIcon sx={{ fontSize: 16, color: '#757575' }} />
            <Typography variant="body2" sx={{ flex: 1, color: '#333' }}>
              {proposal.property.address}
            </Typography>
            <ChevronRightIcon sx={{ fontSize: 18, color: '#999' }} />
          </Box>
          
          <Typography variant="caption" color="text.secondary">
            {moment(proposal.createdAt).tz('America/Los_Angeles').format('MM/DD/YYYY hh:mm A')}
          </Typography>
        </CardContent>
      </Link>
    </Card>
  );
};

const ClientProposalsTable = ({ proposals }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination();

  return (
    <Fragment>
      {!isMobile && (
        <TableContainer component={Paper} sx={table_container_base}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell width="15%">Status</TableCell>
                <TableCell width="15%">Public ID</TableCell>
                <TableCell width="35%">Property</TableCell>
                <TableCell width="15%" align="center">Paid</TableCell>
                <TableCell width="20%">Created At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {proposals.length ? (
                proposals.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((proposal) => (
                  <TableRow key={proposal.id}>
                    <TableCell>
                      <Link href={`/proposals/${proposal.id}`} sx={{ fontWeight: 'bold' }} className="full-col-flex-link" underline="none" color="rgba(0,0,0,0.87)">
                        {proposal.status === 'Active' && <><CircleIcon sx={{ color: 'green', mr: 1, width: 15 }} /> Active</>}
                        {proposal.status === 'Pending' && <><CircleIcon sx={{ color: 'orange', mr: 1, width: 15 }} /> Pending</>}
                        {proposal.status === 'Cold' && <><CircleIcon sx={{ color: '#42a5f5', mr: 1, width: 15 }} /> Cold</>}
                        {proposal.status === 'Completed' && <><CheckCircleIcon sx={{ color: 'green', mr: 1, width: 15 }} /> Completed</>}
                        {proposal.status === 'Cancelled' && <><CancelIcon sx={{ color: 'red', mr: 1, width: 15 }} /> Cancelled</>}
                        {proposal.status === 'Lost' && <><CircleIcon sx={{ color: '#e91e63', mr: 1, width: 15 }} /> Lost</>}
                      </Link>
                    </TableCell>
                    <TableCell><Link href={`/proposals/${proposal.id}`} color="rgba(0,0,0,0.87)" underline="none" className="full-col-flex-link">{proposal.publicId}</Link></TableCell>
                    <TableCell><Link href={`/proposals/${proposal.id}`} color="rgba(0,0,0,0.87)" underline="none" className="full-col-flex-link">{proposal.property.address}</Link></TableCell>
                    <TableCell align="center">
                      <Link href={`/proposals/${proposal.id}`} color="rgba(0,0,0,0.87)" underline="none" className="full-col-flex-link" sx={{ justifyContent: 'center' }}>
                        <AttachMoneyIcon sx={{ color: proposal.additionalNotes?.payment?.completed ? '#4caf50' : '#e0e0e0', fontSize: 24 }} />
                      </Link>
                    </TableCell>
                    <TableCell><Link href={`/proposals/${proposal.id}`} color="rgba(0,0,0,0.87)" underline="none" className="full-col-flex-link">{moment(proposal.createdAt).tz('America/Los_Angeles').format('MM/DD/YYYY hh:mm A')}</Link></TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center' }}><strong>No Data</strong></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {isMobile && (
        <Box>
          {proposals.length ? (
            proposals.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((proposal) => (
              <MobileProposalCard key={proposal.id} proposal={proposal} />
            ))
          ) : (
            <Typography variant="body2" sx={{ textAlign: 'center', py: 3 }}>No proposals</Typography>
          )}
        </Box>
      )}

      {!isMobile && (
        <TablePagination rowsPerPageOptions={[10, 25, 50, 100]} component="div" count={proposals.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} labelRowsPerPage="Per page" />
      )}

      {isMobile && proposals.length > rowsPerPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
          <Button size="small" variant="outlined" onClick={() => handleChangePage(null, page - 1)} disabled={page === 0}>Prev</Button>
          <Typography variant="body2" sx={{ alignSelf: 'center' }}>{page + 1}/{Math.ceil(proposals.length / rowsPerPage)}</Typography>
          <Button size="small" variant="outlined" onClick={() => handleChangePage(null, page + 1)} disabled={(page + 1) * rowsPerPage >= proposals.length}>Next</Button>
        </Box>
      )}
    </Fragment>
  );
};

export default ClientProposalsTable;
